import React, { useRef, useEffect, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, SxProps, Theme } from '@mui/material';

export interface OdometerProps {
  /**
   * The numeric value to display
   */
  value: number;
  /**
   * Locale for number formatting (e.g., 'en-US', 'de-DE', 'fr-FR')
   */
  locale?: string;
  /**
   * NumberFormat options for formatting
   */
  numberFormatOptions?: Intl.NumberFormatOptions;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether to animate from 0 on mount
   */
  animateOnMount?: boolean;
  /**
   * MUI sx prop for styling the container
   */
  sx?: SxProps<Theme>;
  /**
   * Styles for individual elements
   */
  styles?: {
    container?: SxProps<Theme>;
    digitContainer?: SxProps<Theme>;
    digitRibbon?: SxProps<Theme>;
    digit?: SxProps<Theme>;
    literalSegment?: SxProps<Theme>;
    selectableText?: SxProps<Theme>;
  };
}

interface DigitSegment {
  type: 'digit' | 'decimal' | 'thousands' | 'literal';
  value: string;
  position: number;
}

interface ParsedFormat {
  segments: DigitSegment[];
  digitCount: number;
  decimalPlaces: number;
}

const OdometerContainer = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: "'Courier New', monospace",
  fontWeight: 'bold',
  lineHeight: 1,
  overflow: 'hidden',
  position: 'relative',
});

const DigitContainer = styled(Box)({
  display: 'inline-block',
  position: 'relative',
  overflow: 'hidden',
  width: '0.6em',
  height: '1em', // Full height to show one digit properly
  textAlign: 'center',
});

const DigitRibbon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'offset',
})<{ offset: number }>(({ offset }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '10em', // 10 digits × 1em each = 10em total height
  transform: `translateY(${offset}em)`,
  transition: 'transform 0.5s ease-out',
}));

const Digit = styled(Typography)({
  height: '1em', // Each digit is 1em tall
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 1,
  margin: 0,
  padding: 0,
});

const LiteralSegment = styled(Typography)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '1em',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 1,
  padding: '0 0.02em',
  margin: 0,
});

// Selectable text overlay for clipboard functionality
const SelectableText = styled(Typography)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  color: 'transparent',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 1,
  userSelect: 'text',
  cursor: 'text',
  zIndex: 1,
  margin: 0,
  padding: 0,
  
  '&::selection': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    color: 'inherit',
  },
  
  '&::-moz-selection': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    color: 'inherit',
  },
});

const Odometer: React.FC<OdometerProps> = ({ 
  value, 
  locale = 'en-US',
  numberFormatOptions = {},
  className,
  animateOnMount = true,
  sx,
  styles = {}
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentValue, setCurrentValue] = useState(animateOnMount ? 0 : value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(!animateOnMount);
  const [isVisible, setIsVisible] = useState(false);

  // Create NumberFormat instance
  const numberFormatter = useCallback(() => {
    return new Intl.NumberFormat(locale, numberFormatOptions);
  }, [locale, numberFormatOptions]);

  // Parse formatted number into segments
  const parseFormattedNumber = useCallback((num: number): ParsedFormat => {
    const formatter = numberFormatter();
    const formattedString = formatter.format(num);
    
    const segments: DigitSegment[] = [];
    let digitCount = 0;
    let decimalPlaces = 0;
    let position = 0;

    for (let i = 0; i < formattedString.length; i++) {
      const char = formattedString[i];
      
      if (/\d/.test(char)) {
        segments.push({
          type: 'digit',
          value: char,
          position: position++
        });
        digitCount++;
      } else if (char === '.' || char === ',') {
        // Determine if this is decimal or group separator based on context
        const isDecimal = i > 0 && /\d/.test(formattedString[i - 1]) && 
                         i < formattedString.length - 1 && /\d/.test(formattedString[i + 1]);
        
        if (isDecimal) {
          segments.push({
            type: 'decimal',
            value: char,
            position: position++
          });
        } else {
          segments.push({
            type: 'thousands',
            value: char,
            position: position++
          });
        }
      } else {
        // Treat any other character as literal (currency symbols, spaces, etc.)
        segments.push({
          type: 'literal',
          value: char,
          position: position++
        });
      }
    }

    // Count decimal places
    const decimalIndex = segments.findIndex(s => s.type === 'decimal');
    if (decimalIndex !== -1) {
      decimalPlaces = segments.slice(decimalIndex + 1).filter(s => s.type === 'digit').length;
    }

    return { segments, digitCount, decimalPlaces };
  }, [numberFormatter]);

  // Format number according to parsed format
  const formatNumber = useCallback((num: number, parsedFormat: ParsedFormat): string[] => {
    const { segments } = parsedFormat;
    
    // Handle negative numbers
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    
    // Format the number using Intl.NumberFormat
    const formatter = numberFormatter();
    const formattedString = formatter.format(absNum);
    
    // Convert to array of characters
    const chars = formattedString.split('');
    
    // Map characters to segments
    const result: string[] = [];
    let charIndex = 0;
    
    for (const segment of segments) {
      if (segment.type === 'digit') {
        result.push(chars[charIndex] || '0');
        charIndex++;
      } else {
        result.push(segment.value);
      }
    }
    
    return result;
  }, [numberFormatter]);

  // Generate clipboard-friendly text
  const getClipboardText = useCallback((num: number): string => {
    const formatter = numberFormatter();
    return formatter.format(num);
  }, [numberFormatter]);

  // Calculate ribbon offset for a digit
  const getDigitOffset = useCallback((digit: string): number => {
    const digitValue = parseInt(digit, 10);
    // Each digit is 1em tall, so digit 0 is at 0, digit 1 is at -1em, etc.
    // The ribbon height is 10em (10 digits × 1em each)
    return -digitValue * 1;
  }, []);

  // Animate to new value
  const animateToValue = useCallback((newValue: number) => {
    if (newValue === currentValue) return;
    
    setIsAnimating(true);
    setCurrentValue(newValue);
    
    // Reset animation state after transition
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  }, [currentValue]);

  // Intersection Observer for visibility-based animation
  useEffect(() => {
    if (!animateOnMount || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            setIsVisible(true);
            // Start animation from 0 to target value with a slight delay for better UX
            setTimeout(() => {
              animateToValue(value);
            }, 200);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '50px', // Start animation slightly before element is fully visible
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [animateOnMount, hasAnimated, value, animateToValue]);

  // Update value when prop changes (if not animating on mount)
  useEffect(() => {
    if (!animateOnMount || hasAnimated) {
      animateToValue(value);
    }
  }, [value, animateToValue, animateOnMount, hasAnimated]);

  const parsedFormat = parseFormattedNumber(value);
  const formattedDigits = formatNumber(currentValue, parsedFormat);
  const clipboardText = getClipboardText(currentValue);

  return (
    <OdometerContainer 
      ref={containerRef}
      className={className}
      sx={styles.container || sx}
    >
      {/* Selectable text overlay for clipboard functionality */}
      <SelectableText variant="h3" sx={styles.selectableText}>
        {clipboardText}
      </SelectableText>
      
      {parsedFormat.segments.map((segment, index) => {
        if (segment.type === 'digit') {
          const digit = formattedDigits[index];
          const offset = getDigitOffset(digit);
          
          return (
            <DigitContainer key={`digit-${index}`} sx={styles.digitContainer}>
              <DigitRibbon offset={offset} sx={styles.digitRibbon}>
                {Array.from({ length: 10 }, (_, i) => (
                  <Digit key={i} variant="h3" sx={styles.digit}>
                    {i}
                  </Digit>
                ))}
              </DigitRibbon>
            </DigitContainer>
          );
        } else {
          return (
            <LiteralSegment key={`literal-${index}`} variant="h3" sx={styles.literalSegment}>
              {segment.value}
            </LiteralSegment>
          );
        }
      })}
    </OdometerContainer>
  );
};

export { Odometer }; 